import { useState, useEffect, useMemo } from "react"
import { Mail, Send, Eye, Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Company } from "@/types/company"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useToast } from "@/components/ui/use-toast"
import { emailService, EmailTemplate, BulkEmailRecipient } from "@/services/emailService"
import { emailTemplateService } from "@/services/emailTemplateService"
import { TemplatePreviewDialog } from "@/components/email/TemplatePreviewDialog"

interface BulkEmailDialogProps {
  isOpen: boolean
  onClose: () => void
  companies: Company[]
}

interface AdminRecipient {
  email: string
  username: string
  companyName: string
  companyId: string
}

const templateIcons: Record<string, string> = {
  'update': '🚀',
  'fixed': '✅',
  'new-feature': '✨',
  'info': '📢'
}

export function BulkEmailDialog({
  isOpen,
  onClose,
  companies,
}: BulkEmailDialogProps) {
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<"template" | "custom">("template")
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewHtml, setPreviewHtml] = useState("")

  // Custom email form
  const [customForm, setCustomForm] = useState({
    subject: "",
    title: "",
    content: "",
    imageUrl: "",
    buttonText: "",
    buttonUrl: ""
  })

  // Filters
  const [filters, setFilters] = useState({
    premium: true,
    trial: true,
    expired: false,
    passive: false
  })

  // Seçili email'ler (email bazlı)
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set())

  // Şablonları getir
  useEffect(() => {
    if (isOpen) {
      fetchTemplates()
    }
  }, [isOpen])

  const fetchTemplates = async () => {
    try {
      setIsLoadingTemplates(true)
      const response = await emailTemplateService.getAll()
      setTemplates(response.data.data.filter(t => t.isActive))
    } catch (error) {
      console.error("Şablonlar yüklenirken hata:", error)
    } finally {
      setIsLoadingTemplates(false)
    }
  }

  // Filtrelere göre firmaları filtrele
  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const isExpired = new Date(company.license.expiryDate) < new Date()

      if (filters.premium && company.license.type === "premium" && !isExpired) return true
      if (filters.trial && company.license.type === "trial" && !isExpired) return true
      if (filters.expired && isExpired) return true
      if (filters.passive && company.license.isActive === false) return true

      return false
    })
  }, [companies, filters])

  // Tüm admin'leri flat liste olarak al
  const allAdmins = useMemo((): AdminRecipient[] => {
    const admins: AdminRecipient[] = []

    filteredCompanies.forEach(company => {
      if (company.admins && company.admins.length > 0) {
        company.admins.forEach(admin => {
          if (admin.email) {
            admins.push({
              email: admin.email,
              username: admin.name || 'Kullanıcı',
              companyName: company.name,
              companyId: company._id
            })
          }
        })
      }
    })

    return admins
  }, [filteredCompanies])

  // Filtrelere göre admin'leri seç
  useEffect(() => {
    const emails = new Set<string>()
    allAdmins.forEach(admin => {
      emails.add(admin.email)
    })
    setSelectedEmails(emails)
  }, [allAdmins])

  const toggleEmail = (email: string) => {
    setSelectedEmails(prev => {
      const newSet = new Set(prev)
      if (newSet.has(email)) {
        newSet.delete(email)
      } else {
        newSet.add(email)
      }
      return newSet
    })
  }

  // Toplam yönetici sayısı
  const totalAdminCount = allAdmins.length
  const selectedEmailCount = selectedEmails.size

  // Alıcı listesini oluştur
  const getRecipients = (): BulkEmailRecipient[] => {
    return allAdmins
      .filter(admin => selectedEmails.has(admin.email))
      .map(admin => ({
        email: admin.email,
        username: admin.username,
        companyName: admin.companyName
      }))
  }

  // Önizleme
  const handlePreview = async () => {
    if (activeTab === "template" && selectedTemplate) {
      try {
        const response = await emailTemplateService.preview(selectedTemplate.slug)
        setPreviewHtml(response.data.data.html)
        setIsPreviewOpen(true)
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Hata",
          description: "Önizleme yüklenirken hata oluştu."
        })
      }
    }
  }

  // Email gönder
  const handleSend = async () => {
    const recipients = getRecipients()

    if (recipients.length === 0) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "En az bir alıcı seçmelisiniz."
      })
      return
    }

    try {
      setIsSending(true)

      let response

      if (activeTab === "template" && selectedTemplate) {
        response = await emailService.sendBulkWithTemplate(selectedTemplate.slug, recipients)
      } else {
        if (!customForm.subject.trim() || !customForm.content.trim()) {
          toast({
            variant: "destructive",
            title: "Hata",
            description: "Konu ve içerik alanları zorunludur."
          })
          return
        }

        response = await emailService.sendBulkCustom({
          subject: customForm.subject,
          title: customForm.title || customForm.subject,
          content: customForm.content,
          imageUrl: customForm.imageUrl || undefined,
          buttonText: customForm.buttonText || undefined,
          buttonUrl: customForm.buttonUrl || undefined
        }, recipients)
      }

      const result = response.data.data

      toast({
        title: "Email Gönderildi",
        description: `${result.success} email başarıyla gönderildi${result.failed > 0 ? `, ${result.failed} başarısız` : ""}.`
      })

      onClose()
    } catch (error) {
      console.error("Email gönderilirken hata:", error)
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Email gönderilirken bir hata oluştu."
      })
    } finally {
      setIsSending(false)
    }
  }

  // Firma başına admin sayısını hesapla
  const companiesWithoutAdmins = filteredCompanies.filter(c => !c.admins || c.admins.length === 0 || !c.admins.some(a => a.email)).length

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Toplu E-posta Gönder
            </DialogTitle>
            <DialogDescription>
              {companies.length} firma içinden {filteredCompanies.length} firma filtrelendi ({totalAdminCount} yönetici)
              {companiesWithoutAdmins > 0 && (
                <span className="text-red-500 block mt-1 text-xs">
                  ({companiesWithoutAdmins} firmada yönetici e-postası yok)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Alıcı Filtreleri */}
            <div className="flex flex-wrap gap-4 px-1">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="premium"
                  checked={filters.premium}
                  onChange={() => setFilters({...filters, premium: !filters.premium})}
                />
                <Label htmlFor="premium" className="text-sm font-medium">
                  Premium ({companies.filter(c => c.license.type === "premium").length})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="trial"
                  checked={filters.trial}
                  onChange={() => setFilters({...filters, trial: !filters.trial})}
                />
                <Label htmlFor="trial" className="text-sm font-medium">
                  Trial ({companies.filter(c => c.license.type === "trial").length})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="expired"
                  checked={filters.expired}
                  onChange={() => setFilters({...filters, expired: !filters.expired})}
                />
                <Label htmlFor="expired" className="text-sm font-medium">
                  Süresi Dolan ({companies.filter(c => new Date(c.license.expiryDate) < new Date()).length})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="passive"
                  checked={filters.passive}
                  onChange={() => setFilters({...filters, passive: !filters.passive})}
                />
                <Label htmlFor="passive" className="text-sm font-medium">
                  Pasif ({companies.filter(c => c.license.isActive === false).length})
                </Label>
              </div>
            </div>

            {/* E-posta Alıcıları - Tüm Yöneticiler */}
            <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
              <div className="text-sm font-medium mb-2">
                E-posta Alıcıları ({selectedEmailCount} / {totalAdminCount} yönetici)
              </div>
              <div className="flex flex-wrap gap-2">
                {allAdmins.map((admin, index) => {
                  if (!selectedEmails.has(admin.email)) return null

                  return (
                    <div
                      key={`${admin.companyId}-${admin.email}-${index}`}
                      className="flex items-center px-2 py-1 bg-gray-200 rounded-md text-xs cursor-pointer hover:bg-gray-300"
                      onClick={() => toggleEmail(admin.email)}
                      title={`${admin.email} - ${admin.companyName}`}
                    >
                      <span className="font-medium">{admin.username}</span>
                      <span className="text-gray-500 ml-1">({admin.companyName})</span>
                      <span className="ml-1 text-gray-500">×</span>
                    </div>
                  )
                })}
              </div>
              {selectedEmailCount === 0 && (
                <p className="text-sm text-muted-foreground">Seçili alıcı yok</p>
              )}
            </div>

            {/* Tab: Şablon / Custom */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "template" | "custom")}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="template">Şablon Seç</TabsTrigger>
                <TabsTrigger value="custom">Özel İçerik</TabsTrigger>
              </TabsList>

              <TabsContent value="template" className="space-y-4">
                {isLoadingTemplates ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {templates.map((template) => (
                      <Card
                        key={template._id}
                        className={`cursor-pointer transition-all ${
                          selectedTemplate?.slug === template.slug
                            ? "ring-2 ring-primary bg-primary/5"
                            : "hover:bg-muted/50"
                        }`}
                        onClick={() => setSelectedTemplate(template)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{templateIcons[template.slug]}</span>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{template.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {template.subject}
                              </p>
                            </div>
                            {selectedTemplate?.slug === template.slug && (
                              <Badge variant="secondary" className="text-xs">Seçili</Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {selectedTemplate && (
                  <div className="bg-muted p-3 rounded-lg space-y-2">
                    <p className="text-sm font-medium">Seçilen Şablon: {selectedTemplate.name}</p>
                    <p className="text-xs text-muted-foreground">Konu: {selectedTemplate.subject}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      İçerik: {selectedTemplate.content}
                    </p>
                    <Button variant="outline" size="sm" onClick={handlePreview}>
                      <Eye className="h-4 w-4 mr-1" />
                      Önizle
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="custom" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="customSubject">Email Konusu *</Label>
                  <Input
                    id="customSubject"
                    placeholder="E-posta Konusu"
                    value={customForm.subject}
                    onChange={(e) => setCustomForm({...customForm, subject: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customTitle">Başlık</Label>
                  <Input
                    id="customTitle"
                    placeholder="Email içi başlık (opsiyonel)"
                    value={customForm.title}
                    onChange={(e) => setCustomForm({...customForm, title: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customContent">İçerik *</Label>
                  <Textarea
                    id="customContent"
                    placeholder="E-posta içeriği... {{firma}} kullanarak firma adını ekleyebilirsiniz."
                    value={customForm.content}
                    onChange={(e) => setCustomForm({...customForm, content: e.target.value})}
                    rows={5}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customImage">Resim URL (Opsiyonel)</Label>
                  <Input
                    id="customImage"
                    placeholder="https://example.com/image.png"
                    value={customForm.imageUrl}
                    onChange={(e) => setCustomForm({...customForm, imageUrl: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customButton">Buton Metni</Label>
                    <Input
                      id="customButton"
                      placeholder="Örn: Detayları Gör"
                      value={customForm.buttonText}
                      onChange={(e) => setCustomForm({...customForm, buttonText: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customButtonUrl">Buton Linki</Label>
                    <Input
                      id="customButtonUrl"
                      placeholder="https://panel.cloudmedia.com.tr"
                      value={customForm.buttonUrl}
                      onChange={(e) => setCustomForm({...customForm, buttonUrl: e.target.value})}
                      disabled={!customForm.buttonText}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isSending}>
              İptal
            </Button>
            <Button
              onClick={handleSend}
              disabled={
                isSending ||
                selectedEmailCount === 0 ||
                (activeTab === "template" && !selectedTemplate) ||
                (activeTab === "custom" && (!customForm.subject.trim() || !customForm.content.trim()))
              }
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1" />
                  {selectedEmailCount > 0 ? `${selectedEmailCount} Yöneticiye Gönder` : "Gönder"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <TemplatePreviewDialog
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        html={previewHtml}
        templateName={selectedTemplate?.name || ""}
      />
    </>
  )
}
